import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import { selectAiProduct } from '@store/ai-product/selectors';
import { useRef, useState, useMemo, useEffect } from 'react';
import classNames from 'classnames';
import ProductImageEditor from '@pages/ai-product/image-editor/ProductImageEditor';
import ProductImageFormDialog from '@pages/ai-product/ProductImageFormDialog';
import {
  processAiProduct, renderAiProduct,
  updateAiProduct,
} from '@store/ai-product/actions';
import { actions } from '@store/companies';
import { ROUTES } from '@app/routes/routesUrls';
import useNavigate from '@shared/router/useNavigate';
import Button from '@shared/components/button';
import useOnClickOutside from '@shared/hooks';
import Backdrop from '@shared/components/backdrop';
import CircularProgress from '@shared/components/circular-progress';
import ProductTextField from '@pages/ai-product/background-step/ProductTextField';
import EnvironmentTextField from '@pages/ai-product/background-step/EnvironmentTextField';
import TextField from '@shared/components/text-field';
import CreateIcon from '@resources/icons/CreateIcon';
import GalleryIcon from '@resources/icons/GalleryIcon';
import CheckIcon from '@resources/icons/CheckIcon';
import ExpandIcon from '@resources/icons/ExpandIcon';
import VideoViewTextField from './VideoViewTextField';
import appendUrlParams from '@shared/utils/appendUrlParams';
import LimitBanner from '@shared/components/page-parts/LimitBanner';
import AIProduct from '..';

const useStyles = makeStyles((theme) => ({
  pageWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    fontSize: '14px',
    padding: '16px',
    width: '100%',
    height: '100%',
    overflow: 'auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '16px',
    color: '#334D6E',
    fontWeight: 700,
    textDecoration: 'underline',
    '& > div': {
      width: '100%',
      maxWidth: '200px',
    },
    marginBottom: '16px',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '20px',
  },
  contentHeader: {
    display: 'inline-flex',
    flexDirection: 'column',
    gap: '6px',
    fontSize: '20px',
    fontWeight: 700,
  },
  contentSubHeader: {
    color: '#9CACC2',
    fontSize: '16px',
    fontWeight: 400,
  },
  imagesWrapper: {
    display: 'flex',
    gap: '20px',
    maxWidth: '100%',
    overflowX: 'auto',
    padding: '20px',
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: '4px',
  },
  uploadImage: {
    width: '176px',
    height: '100px',
    objectFit: 'cover',
    borderRadius: '4px',
    border: '1px solid #E3E9F1',
    flex: '0 0 176px',
    position: 'relative',
    '& > img': {
      width: '100%',
      height: '100%',
      borderRadius: '4px',
      objectFit: 'contain',
    },
  },
  uploadImageModal: {
    width: '186px',
    height: '186px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
    borderRadius: '4px',
    objectFit: 'cover',
    flex: '0 0 186px',
    position: 'relative',
    '& > img': {
      width: '100%',
      filter: 'opacity(0.5)',
      height: '100%',
      borderRadius: '4px',
      objectFit: 'cover',
    },
    '&.active': {
      border: `2px solid ${theme.palette.common.darkGrey}`,
      '& > img': {
        filter: 'unset',
      },
    },
  },
  imageEditorWrapper: {
    display: 'flex',
    marginTop: '20px',
    gap: '16px',
    flex: '1',
  },
  productImageEditorWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    flex: '1 1 auto',
    maxWidth: '95%',
  },
  submitButton: {
    alignSelf: 'center',
  },
  imageList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    overflow: 'auto',
  },
  actions: {
    display: 'flex',
    marginTop: '50px',
    gap: '16px',
    '& > button': {
      minWidth: '142px',
      width: '100%',
    },
    '& > button:first-child': {
      borderColor: '#192A3E',
      color: '#192A3E',
    },
  },
  toolBarWrapper: {
    display: 'flex',
    gap: '20px',
    width: '100%',
    '& > button': {
      minWidth: '200px',
      height: '42px',
      width: '100%',
    },
  },
  buttonCreate: {
    fontSize: '13px',
    fontWeight: 500,
    display: 'inline-flex',
    flexDirection: 'row',
    gap: '10px',
    lineHeight: 'normal',
    verticalAlign: 'middle',
    alignItems: 'center',
    '& > svg': {
      fontSize: '1.8rem',
      '& > path': {
        stroke: '#fff',
      },
    },
    '& > button': {
      maxWidth: 'unset',
    },
  },
  chooseImages: {
    maxWidth: '200px',
    maxHeight: '42px',
    width: '100%',
    height: '100%',
    display: 'inline-flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '0 12px',
    fontSize: '13px',
    fontWeight: 500,
    gap: '5px',
    justifyContent: 'flex-start',
    border: '1px solid #C3D0E1',
    borderRadius: '4px',
    color: '#192A3E',
    '&:after': {
      content: "''",
      display: 'block',
      width: 0,
      height: 0,
      borderLeft: '.5rem solid transparent',
      borderRight: '.5rem solid transparent',
      borderTop: '.5rem solid #192A3E',
      marginLeft: 'auto',
    },
    '&.active': {
      borderColor: '#00C98F',
      color: '#00C98F',
      '&:after': {
        transform: 'rotate(180deg)',
        borderTopColor: '#00C98F',
      },
      '& > svg path': {
        fill: '#00C98F',
      },
    },
    '&.selected': {
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
      '&:after': {
        borderTopColor: theme.palette.primary.main,
      },
      '& > svg path': {
        fill: theme.palette.primary.main,
      },
    },
  },
  chooseImageEmpty: {
    objectFit: 'cover',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '268px',
    height: '184px',
    border: '1px solid #E3E9F1',
    '& > img': {
      width: '100%',
      height: '100%',
      borderRadius: '4px',
      objectFit: 'cover',
    },
  },
  uploadVideo: {
    width: '268px',
    position: 'relative',
    display: 'flex',
    height: '184px',
    '& > img': {
      width: '100%',
      maxWidth: '268px',
      maxHeight: '184px',
      height: '100%',
      borderRadius: '4px',
      objectFit: 'cover',
    },
    '& > video': {
      width: '100%',
      borderRadius: '4px',
      objectFit: 'cover',
    },
  },
  chooseImageEmptyText: {
    fontSize: '13px',
    fontWeight: 400,
  },
  formDialog: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  colorButton: {
    position: 'relative',
    width: '160px',
  },
  colorPicker: {
    position: 'absolute',
    zIndex: 1,
  },
  backdrop: {
    top: 0,
    '& > img': {
      width: '100%',
      maxWidth: '268px',
      maxHeight: '184px',
      height: '100%',
      borderRadius: '4px',
      position: 'absolute',
      filter: 'opacity(0.5)',
      objectFit: 'cover',
    },
    '& > video': {
      width: '100%',
      borderRadius: '4px',
      objectFit: 'cover',
    },
    '& > span': {
      position: 'absolute',
    },
  },
  iconsWrapper: {
    display: 'flex',
    gap: '8px',
    zIndex: 2,
    position: 'absolute',
    top: '8px',
    right: '6px',
    cursor: 'pointer',
  },
  iconButton: {
    display: 'flex',
    zIndex: 2,
    border: '1px solid #E3E9F1',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#fff',
    borderRadius: '50%',
    padding: '4px',
    '& > svg': {
      color: '#9DA3B2',
    },
  },
  selectIcon: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    padding: '4px',
    position: 'absolute',
    bottom: '8px',
    right: '8px',
    border: 'none',
    zIndex: 2,
    background: '#E3E9F1',
    '& > svg path': {
      fill: '#fff',
      stroke: 'inherit',
    },
    '&.active': {
      background: theme.palette.primary.main,
    },
  },
  autocomplete: {
    width: '200px',
    height: '42px',
  },
}));

export function VideoStep() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { goTo } = useNavigate();
  const settings = useSelector((state) => state.companies.settings);
  const user = useSelector((state) => state.app.user);
  const product = useSelector(selectAiProduct);
  const containerRef = useRef();
  const stageRef = useRef();
  const [isProductImageOpen, setIsProductImageOpen] = useState(false);
  const [activeImage, setActiveImage] = useState(product?.images?.[0] || null);
  const [activeImageVariant, setActiveImageVariant] = useState(0);
  const [showColor, setShowColor] = useState(false);
  const [showImages, setShowImages] = useState(false);
  const [generatingStates, setGeneratingStates] = useState({});
  const [isVideoDialog, setIsVideoDialog] = useState(false);
  const { getCurrentSettings } = actions;

  useEffect(() => {
    dispatch(getCurrentSettings(user.company.id));
  }, [dispatch, user, getCurrentSettings]);

  const isAiProductReadOnly = useMemo(() => {
    return !(settings?.studioConfig?.ai_p_active ?? true);
  }, [settings]);

  useOnClickOutside(containerRef, () => {
    setShowColor(false);
  });

  const handleCancel = () => {
    setIsProductImageOpen(false);
    setIsVideoDialog(false);
  };

  const handleSave = () => {
    dispatch(updateAiProduct(product, null));
    setIsProductImageOpen(false);
    setIsVideoDialog(false);
  };

  const handleCreate = () => {
    product.images.forEach((image) => {
      const selectedLifestyle = image.lifestyle.find((lifestyle) => lifestyle.isVideoUsed);
      if (selectedLifestyle) {
        setGeneratingStates((prev) => ({
          ...prev,
          [`${image.id}-${selectedLifestyle.src}`]: true,
        }));

        const updatedImages = product.images.map((img) => {
          if (img.id === image.id) {
            return {
              ...img,
              lifestyle: img.lifestyle.map((lifestyle, index) => {
                if (index === image.lifestyle.indexOf(selectedLifestyle)) {
                  return {
                    ...lifestyle,
                    isGenerating: true,
                  };
                }
                return lifestyle;
              }),
            };
          }
          return img;
        });

        dispatch(updateAiProduct({
          ...product,
          images: updatedImages,
        }, null, () => {
          dispatch(processAiProduct(product.id, {
            tasks: ['VIDEO_GENERATION'],
            images: [{
              imageId: image.id,
              lifestyleId: selectedLifestyle.id,
            }],
            isBeta: false,
          }));
        }));
      }
    });
  };

  const handleSelectImage = (imageId, variantIndex) => {
    const updatedImages = product.images.map((image) => {
      const currentlySelected = image.lifestyle.find((lifeImage) => lifeImage.isVideoUsed);
      if (currentlySelected && image.id === imageId && image.lifestyle[variantIndex] === currentlySelected) {
        return {
          ...image,
          lifestyle: image.lifestyle.map((lifeImage) => ({
            ...lifeImage,
            isVideoUsed: false,
          })),
        };
      }
      return {
        ...image,
        lifestyle: image.lifestyle.map((lifeImage, index) => ({
          ...lifeImage,
          isVideoUsed: image.id === imageId && index === variantIndex,
        })),
      };
    });
    dispatch(
      renderAiProduct({
        ...product,
        images: updatedImages,
      }),
    );
  };

  const isImageSelected = product.images.some((image) => image.lifestyle.some((lifestyle) => lifestyle.isVideoUsed));

  const imageForTextGen = product.images[0];

  const isVideoCreated = product.images.some((image) => image.lifestyle.some((lifestyle) => lifestyle.videoSrc));

  const handleExpandClick = (image, index, isVideo = false) => (e) => {
    e.stopPropagation();
    setIsProductImageOpen(true);
    setActiveImage(image);
    setActiveImageVariant(index);
    setIsVideoDialog(isVideo);
  };

  const handleCheckClick = (imageId, variantIndex) => (e) => {
    e.stopPropagation();
    handleSelectImage(imageId, variantIndex);
  };

  const hasVideoGenerationProcess = product.images.some((image) => image.state === 'VIDEO_GENERATION');

  return (
    <div className={ classes.pageWrapper }>
      <AIProduct />
      <div className={ classes.header }>
        Product Detail Page Content Pack
      </div>
      <LimitBanner show={ isAiProductReadOnly } />
      <div className={ classes.content }>
        <div className={ classes.contentHeader }>
          Step 3. Short Videos
          <div className={ classes.contentSubHeader }>
            Select photos from which videos will be generated.
          </div>
        </div>
        <div className={ classes.toolBarWrapper }>
          <div
            className={ classNames(classes.chooseImages, { selected: isImageSelected }) }
            onClick={ () => setShowImages((prev) => !prev) }
          >
            <GalleryIcon fontSize="large" />
            Choose Images
          </div>
          <div className={ classes.autocomplete }>
            <VideoViewTextField
              value={ product.lifestyleVideoPreset?.name }
              onChange={ (name) => {
                dispatch(renderAiProduct({
                  ...product,
                  lifestyleVideoPreset: { name },
                }));
              } }
            />
          </div>
          <Button
            disabled={ !product.category || !product.lifestyleVideoPreset || !isImageSelected || hasVideoGenerationProcess || isAiProductReadOnly }
            onClick={ handleCreate }
          >
            <div className={ classes.buttonCreate }>
              <CreateIcon />
              Create
            </div>
          </Button>
        </div>
        {
          showImages && (
            <div className={ classes.imagesWrapper }>
              { product.images.map((image) => {
                return (
                  <>
                    { !!image.lifestyle?.length && image.lifestyle.map((lifestyle, index) => {
                      const isSelected = lifestyle.isVideoUsed;
                      return (
                        <div
                          key={ `${image.id}${index}` }
                          className={ classes.uploadImage }
                        >
                          <img src={ appendUrlParams(lifestyle.src, { size: 256 }) } />
                          <div className={ classes.iconsWrapper }>
                            <div
                              className={ classes.iconButton }
                              onClick={ handleExpandClick(image, index) }
                            >
                              <ExpandIcon />
                            </div>
                          </div>
                          <div
                            className={ classNames(classes.selectIcon, { active: isSelected }) }
                            onClick={ handleCheckClick(image.id, index) }
                          >
                            <CheckIcon size="small" />
                          </div>
                        </div>
                      );
                    })}
                  </>
                );
              })}
            </div>
          )
        }
        <div className={ classes.chooseImageEmpty }>
          {!isImageSelected && (
            <span className={ classes.chooseImageEmptyText }>
              Choose <strong>Image</strong> first
            </span>
          )}
          {isImageSelected && (
            <div className={ classes.videoWrapper }>
              {product.images.map((image) => {
                const selectedLifestyle = image.lifestyle.find((lifestyle) => lifestyle.isVideoUsed);
                if (selectedLifestyle) {
                  return (
                    <div
                      key={ `${image.id}-${selectedLifestyle.src}` }
                      className={ classes.uploadVideo }
                    >
                      {selectedLifestyle.videoSrc ? (
                        <>
                          <div className={ classes.iconsWrapper }>
                            <div
                              className={ classes.iconButton }
                              onClick={ handleExpandClick(image, image.lifestyle.indexOf(selectedLifestyle), true) }
                            >
                              <ExpandIcon />
                            </div>
                            { !isAiProductReadOnly && (
                            <div
                              className={ classes.iconButton }
                              onClick={ handleCreate }
                            >
                              <CreateIcon />
                            </div>
                            )}
                          </div>
                          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                          <video autoPlay playsInline loop muted>
                            <source src={ selectedLifestyle.videoSrc } type="video/mp4" />
                            Your browser does not support the video.
                          </video>
                          <div
                            className={ classNames(classes.selectIcon, { active: selectedLifestyle }) }
                            onClick={ (e) => {
                              e.stopPropagation();
                              const updatedImages = product.images.map(img => {
                                if (img.id === image.id) {
                                  return {
                                    ...img,
                                    lifestyle: img.lifestyle.map(lifestyle => ({
                                      ...lifestyle,
                                      isVideoUsed: lifestyle.id === selectedLifestyle.id ? !lifestyle.isVideoUsed : false,
                                    })),
                                  };
                                }
                                return img;
                              });
                              dispatch(renderAiProduct({
                                ...product,
                                images: updatedImages,
                              }));
                            } }
                          >
                            <CheckIcon size="small" />
                          </div>
                        </>
                      ) : hasVideoGenerationProcess ? (
                        <>
                          <img src={ appendUrlParams(selectedLifestyle.src, { size: 512 }) } alt="Selected lifestyle" />
                          <Backdrop className={ classes.backdrop }>
                            <CircularProgress size="large" />
                            Video Generation...
                          </Backdrop>
                        </>
                      ) : (
                        <>
                          <img src={ appendUrlParams(selectedLifestyle.src, { size: 512 }) } alt="Selected lifestyle" />
                          <Backdrop className={ classes.backdrop }>
                            <span className={ classes.chooseImageEmptyText }>
                              Press <strong>Create</strong> button
                            </span>
                          </Backdrop>
                        </>
                      )}
                    </div>
                  );
                }
                return null;
              })}
            </div>
          )}
        </div>
        <div className={ classes.actions }>
          <Button
            color="secondary"
            variant="outlined"
            onClick={ () => {
              goTo(`${ROUTES.AI_PRODUCT.ROOT}${ROUTES.AI_PRODUCT.BACKGROUND(product.id)}`);
            } }
          >
            Back
          </Button>
          <Button
            disabled={ !isVideoCreated || hasVideoGenerationProcess }
            onClick={ () => {
              dispatch(updateAiProduct(product, null, () => {
                if (!product.texts.length) {
                  dispatch(processAiProduct(product.id, {
                    tasks: ['TEXT_GENERATION'],
                    images: [{
                      imageId: imageForTextGen.id,
                    }],
                    isBeta: false,
                  }));
                }
                goTo(`${ROUTES.AI_PRODUCT.ROOT}${ROUTES.AI_PRODUCT.TEXT(product.id)}`);
              }));
            } }
          >
            Next
          </Button>
          <Button
            color="secondary"
            variant="outlined"
            onClick={ () => {
              dispatch(updateAiProduct(product, null, () => {
                if (!product.texts.length && !isAiProductReadOnly) {
                  dispatch(processAiProduct(product.id, {
                    tasks: ['TEXT_GENERATION'],
                    images: [{
                      imageId: imageForTextGen.id,
                    }],
                    isBeta: false,
                  }));
                  goTo(`${ROUTES.AI_PRODUCT.ROOT}${ROUTES.AI_PRODUCT.TEXT(product.id)}`);
                } else if (product.texts.length) {
                  goTo(`${ROUTES.AI_PRODUCT.ROOT}${ROUTES.AI_PRODUCT.TEXT(product.id)}`);
                } else {
                  goTo(`${ROUTES.AI_PRODUCT.ROOT}${ROUTES.AI_PRODUCT.CAPTION(product.id)}`);
                }
              }));
            } }
          >
            Skip
          </Button>
        </div>
      </div>
      <ProductImageFormDialog
        Form="div"
        FormProps={ {
          className: classes.formDialog,
        } }
        open={ isProductImageOpen }
        content={ (
          <div className={ classes.imageEditorWrapper }>
            {!isVideoDialog && (
              <div className={ classes.imageList }>
              {product.images.flatMap((image) => image.lifestyle?.map((lifestyle, index) => (
                  <div
                    key={ `lifestyle-${image.id}-${index}` }
                    onClick={ () => {
                      setActiveImage(image);
                      setActiveImageVariant(index);
                    } }
                    className={ classNames(classes.uploadImageModal, {
                      active: activeImage?.id === image.id && activeImageVariant === index,
                    }) }
                  >
                    <img src={ lifestyle.src } alt={ `Variant ${index}` } />
                  </div>
              )) ?? [])}
            </div>
            )}
            <div className={ classes.productImageEditorWrapper }>
              {activeImage && (
                <ProductImageEditor
                  ref={ stageRef }
                  image={ activeImage }
                  variant={ activeImageVariant }
                  mode={ isVideoDialog ? 'VIDEO' : 'BACKGROUND' }
                  key={ `editor-${activeImage.id}-${activeImageVariant}` }
                />
              )}
            </div>
          </div>
        ) }
        onSave={ handleSave }
        onCancel={ handleCancel }
        onClose={ handleCancel }
        cancelButtonText="Quit"
      />
    </div>
  );
}
